/** Step 2, Site: the site plan (meter, main service panel, conductor path), the service and utility, the design
 * temperatures, and the site photos. On a phone the photos come first (the surveyor's job). */
import { useMemo, useState } from "react";
import { Card, Field, NumberInput, TextInput } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import { ahjLabel, usesBusbarCheck, utilityLabel, type Country } from "../lib/permits";
import { AhjPicker } from "./AhjPicker";
import { useOpenData } from "./data";
import { useFormat } from "./format";
import { Photos } from "./Photos";
import { SitePlan, planShapes, type EquipmentKind, type SiteMarks, type SiteTool } from "./SitePlan";
import { SiteSurroundings } from "./SiteSurroundings";
import { StepChecks, StepHead, useFocusAnchor, type Ws } from "./PermitWorkspace";

export function SiteStep({ ws }: { ws: Ws }) {
  useFocusAnchor(ws.focus);
  const t = useT();
  const f = useFormat();
  /** A value from the server (a temperature, a rating) in the reader's locale, one decimal when it has one. */
  const value = (v: unknown) => (typeof v === "number" ? f.num(v, Number.isInteger(v) ? 0 : 1) : String(v ?? ""));
  const example = t("permit.site.example", { value: f.num(200) });
  const shapes = useMemo(() => planShapes(ws.doc), [ws.doc]);
  const [tool, setTool] = useState<SiteTool>("select");
  const d = ws.details;
  const off = !ws.canEdit;
  const set = (patch: Partial<typeof d>) => ws.setDetails((x) => ({ ...x, ...patch }));
  const country = (ws.inputs.country ?? (ws.preview.data?.country as Country | undefined)) || undefined;
  const resolved = ws.preview.data?.inputs as Record<string, unknown> | undefined;
  const site = ws.preview.data?.electrical.site as Record<string, unknown> | undefined;
  const marks: SiteMarks = { meter: d.meter, main_panel: d.main_panel, conductor_path: d.conductor_path, equipment_locations: d.equipment_locations, property_lines: d.property_lines };
  const [equipmentKind, setEquipmentKind] = useState<EquipmentKind>("inverter");
  const openData = useOpenData(ws.designId, ws.version);
  const context = useMemo(
    () => ({ parcel: d.property_lines?.length ? [] : openData.data?.parcel?.rings, roads: openData.data?.roads }),
    [openData.data, d.property_lines?.length],
  );

  const plan = (
    <Card title={t("permit.site.plan.title")} description={t("permit.site.plan.description")}>
      <div data-anchor="markers" className="pw-anchor">
        <SitePlan
          shapes={shapes}
          marks={marks}
          editable={!off}
          tool={tool}
          onTool={setTool}
          equipmentKind={equipmentKind}
          context={context}
          onMarks={(m) =>
            set({
              meter: m.meter ?? null,
              main_panel: m.main_panel ?? null,
              conductor_path: m.conductor_path ?? [],
              equipment_locations: m.equipment_locations ?? [],
              property_lines: (m.property_lines ?? []).filter((l) => l.length >= 2),
              ...(m.property_lines !== d.property_lines && (m.property_lines?.length ?? 0) > (d.property_lines?.length ?? 0) ? { property_line_source: "drawn" as const, property_line_note: null } : {}),
            })
          }
        />
      </div>
      <div className="pw-form-grid two">
        <Field label={t("permit.site.meterNote")}>
          <TextInput value={d.meter_note ?? ""} disabled={off} maxLength={200} placeholder={t("permit.site.meterNotePlaceholder")} onChange={(e) => set({ meter_note: e.target.value || null })} />
        </Field>
        <Field label={t("permit.site.mainPanelNote")}>
          <TextInput value={d.main_panel_note ?? ""} disabled={off} maxLength={200} placeholder={t("permit.site.mainPanelNotePlaceholder")} onChange={(e) => set({ main_panel_note: e.target.value || null })} />
        </Field>
      </div>
    </Card>
  );
  const surroundings = (
    <SiteSurroundings
      ws={ws}
      openData={openData.data}
      loading={openData.isLoading}
      equipmentKind={equipmentKind}
      onEquipmentKind={setEquipmentKind}
      onPlace={() => setTool("equipment")}
    />
  );
  const service = (
    <Card title={t("permit.site.service.title")} description={t("permit.site.service.description")}>
      <div className="pw-form-grid" data-anchor="service">
        <Field label={t("permit.site.serviceSize")}>
          <NumberInput value={d.service_size_a ?? null} disabled={off} onChange={(v) => set({ service_size_a: v })} suffix="A" min={30} max={2000} placeholder={example} />
        </Field>
        {usesBusbarCheck(country) && (
          <>
            <Field label={t("permit.site.busbar")} hint={t("permit.site.busbarHint")}>
              <NumberInput value={d.busbar_a ?? null} disabled={off} onChange={(v) => set({ busbar_a: v })} suffix="A" min={30} max={2000} placeholder={resolved?.main_panel_a != null ? value(resolved.main_panel_a) : example} />
            </Field>
            <Field label={t("permit.site.mainBreaker")}>
              <NumberInput value={d.main_breaker_a ?? null} disabled={off} onChange={(v) => set({ main_breaker_a: v })} suffix="A" min={15} max={2000} placeholder={resolved?.main_breaker_a != null ? value(resolved.main_breaker_a) : example} />
            </Field>
          </>
        )}
        <Field label={t("permit.site.meterNumber")}>
          <TextInput value={d.utility_meter_number ?? ""} disabled={off} maxLength={60} onChange={(e) => set({ utility_meter_number: e.target.value || null })} />
        </Field>
      </div>
      <div className="pw-form-grid" data-anchor="utility">
        <Field label={t(utilityLabel(country))}>
          <TextInput value={ws.inputs.utility ?? ""} disabled={off} onChange={(e) => ws.setInputs({ utility: e.target.value })} placeholder="" maxLength={160} />
        </Field>
        {(country ?? "US") !== "US" && (
          <Field label={t(ahjLabel(country))}>
            <TextInput value={ws.inputs.ahj ?? ""} disabled={off} onChange={(e) => ws.setInputs({ ahj: e.target.value })} maxLength={160} />
          </Field>
        )}
      </div>
      {(country ?? "US") === "US" && <AhjPicker ws={ws} />}
      <div className="pw-form-grid" data-anchor="temperatures">
        <Field
          label={t("permit.site.designLow")}
          hint={site?.station ? t("permit.site.designLowHint", { station: String(site.station), distance: f.num(site.distance_km as number), temp: value(site.design_low_c) }) : undefined}
        >
          <NumberInput value={ws.inputs.design_low_c ?? null} disabled={off} onChange={(v) => ws.setInputs({ design_low_c: v })} suffix="°C" min={-60} max={30} placeholder={site ? value(site.design_low_c) : ""} />
        </Field>
        <Field label={t("permit.site.designHigh")} hint={site ? t("permit.site.designHighHint", { temp: value(site.design_high_c) }) : undefined}>
          <NumberInput value={ws.inputs.design_high_c ?? null} disabled={off} onChange={(v) => ws.setInputs({ design_high_c: v })} suffix="°C" min={10} max={60} placeholder={site ? value(site.design_high_c) : ""} />
        </Field>
      </div>
      <StepChecks ws={ws} step="site" />
    </Card>
  );
  const photos = (
    <Card title={t("permit.site.photos.title")} description={t("permit.site.photos.description")}>
      <Photos ws={ws} />
    </Card>
  );
  return (
    <div className="pw-step-body">
      <StepHead title={t("permit.step.site")} description={t("permit.site.description")} status={ws.statuses.site} save={ws.siteSave} />
      {ws.phone ? (
        <div className="stack">
          {photos}
          {plan}
          {surroundings}
          {service}
        </div>
      ) : (
        <div className="pw-cols site">
          <div className="stack">
            {plan}
            {surroundings}
            {service}
          </div>
          {photos}
        </div>
      )}
    </div>
  );
}
